import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { MatchDisplay } from "../../model/match-display";
import { CourtReservationService } from "../../services/court-reservation.service";
import { AuthenticationService } from "../../services/authentication.service";

@Component({
  selector: "app-match-history",
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: "./match-history.component.html",
  styleUrl: "./match-history.component.css",
})
export class MatchHistoryComponent implements OnInit {
  previousMatches: MatchDisplay[] = [];

  constructor(
    private courtReservationService: CourtReservationService,
    private authService: AuthenticationService,
  ) {}

  ngOnInit() {
    const currentUser = this.authService.currentUserValue;
    if (!currentUser) return;
    const startDate = new Date(2000, 0, 1);
    const endDate = new Date(2100, 0, 1);
    this.courtReservationService
      .getCourtReservationsForUser(currentUser.id, startDate, endDate)
      .subscribe((reservations: any[]) => {
        const matches = (reservations || [])
          .filter((r: any) => new Date(r.reservation_start_time) < new Date())
          .map((r: any) => this.mapReservation(r, currentUser.id))
          .sort((a: any, b: any) => {
            const da = a.date.split(".").reverse().join("-");
            const db = b.date.split(".").reverse().join("-");
            const diff = new Date(db).getTime() - new Date(da).getTime();
            return diff !== 0 ? diff : b.time.localeCompare(a.time);
          });
        this.previousMatches = matches;
      });
  }

  private mapReservation(res: any, userId: number): MatchDisplay {
    const start = new Date(res.reservation_start_time);
    const players = (res.courtReservationPlayer || []).sort(
      (a: any, b: any) => a.position - b.position,
    );
    const playerInfos = players.map((p: any) => ({
      id: p.player_id,
      name: p.player?.full_name || "Unknown",
    }));
    return {
      date: this.formatDate(start),
      time: this.formatTime(start),
      court: res.court?.club?.name || res.court?.name || String(res.court_id),
      matchType: playerInfos.length > 2 ? "Double" : "Single",
      score: `${res.points ?? 0} points`,
      result: res.finished ? this.determineResult(res, userId) : undefined,
      myTeam: this.getMyTeamNames(playerInfos, userId),
      opponent: this.getOpponentNames(playerInfos, userId),
      expanded: false,
    } as MatchDisplay;
  }

  private formatDate(date: Date): string {
    return `${date.getDate().toString().padStart(2, "0")}.${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}.${date.getFullYear()}`;
  }

  private formatTime(date: Date): string {
    const h = date.getHours().toString().padStart(2, "0");
    const m = date.getMinutes().toString().padStart(2, "0");
    return `${h}:${m}`;
  }

  private determineResult(reservation: any, userId: number): "W" | "L" {
    const players = reservation.courtReservationPlayer || [];
    let myTeam = 0;
    const index = players.findIndex((p: any) => p.player_id === userId);
    if (index >= 0) myTeam = index % 2;
    const opponentTeam = myTeam ^ 1;
    let setsWon = 0;
    for (let i = 0; i < 3; i++) {
      if (
        reservation.sets[myTeam * 3 + i] >
        reservation.sets[opponentTeam * 3 + i]
      ) {
        setsWon += 1;
      }
    }
    return setsWon >= 2 ? "W" : "L";
  }

  private getMyTeamNames(players: any[], currentUserId: number): string {
    const indexCurrentUser = players.findIndex((p) => p.id === currentUserId);
    const myName = players[indexCurrentUser]?.name || "";
    if (players.length === 2) {
      return myName;
    }
    const teammateIndex =
      indexCurrentUser === 0
        ? 2
        : indexCurrentUser === 1
        ? 3
        : indexCurrentUser === 2
        ? 0
        : 1;
    const teammateName = players[teammateIndex]?.name || "";
    return `${myName}&${teammateName}`;
  }

  private getOpponentNames(players: any[], currentUserId: number): string {
    if (players.length === 2) {
      return players.find((p) => p.id !== currentUserId)?.name || "Unknown";
    }
    const indexCurrentUser = players.findIndex((p) => p.id === currentUserId);
    const opponentIndices =
      indexCurrentUser === 0
        ? [1, 3]
        : indexCurrentUser === 1
        ? [0, 2]
        : indexCurrentUser === 2
        ? [1, 3]
        : [0, 2];
    return opponentIndices.map((i) => players[i]?.name || "Unknown").join("&");
  }

  toggleExpand(i: number, match: any): void {
    match.expanded = !match.expanded;
  }

  getWinCount(): number {
    return this.previousMatches.filter((match) => match.result === "W").length;
  }

  getLossCount(): number {
    return this.previousMatches.filter((match) => match.result === "L").length;
  }

  getWinRate(): number {
    const total = this.previousMatches.length;
    if (total === 0) return 0;
    return Math.round((this.getWinCount() / total) * 100);
  }

  getTeamPlayers(teamString: string | undefined): string[] {
    if (!teamString) return [];
    return teamString.includes("&") ? teamString.split("&") : [teamString];
  }
}
